import { SOLVE_AUTOPILOT_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import { AddToolRequest, AutopilotWorkflowModel } from './types';
import {
  AutoflowTool,
  FlowDiagramNode,
  FlowDiagramResponse,
} from 'src/types/workflowBuilderAPITypes';

export interface GetOrCreateAutopilotWorkflowParams {
  configurationId: string;
  version: number;
}

export const emailAutopilotApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    addContextVariable: builder.mutation<
      AutopilotWorkflowModel,
      { autopilotWorkflow: AutopilotWorkflowModel; contextVariableId: string }
    >({
      invalidatesTags: ['AutopilotWorkflow'],
      async onQueryStarted(
        { autopilotWorkflow, contextVariableId },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          emailAutopilotApi.util.updateQueryData(
            'getOrCreateAutopilotWorkflow',
            {
              configurationId: autopilotWorkflow.intent_email_configuration_id,
              version: autopilotWorkflow.intent_email_configuration_version,
            },
            draft => {
              draft.context_variables = [
                ...draft.context_variables,
                {
                  context_variable_id: contextVariableId,
                  context_variable_name: '',
                },
              ];
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ autopilotWorkflow, contextVariableId }) => ({
        method: 'PUT',
        params: { context_variable_id: contextVariableId },
        url: `${SOLVE_AUTOPILOT_BASE_URL}/autopilot-workflow/${autopilotWorkflow.autopilot_workflow_id}/context-variables`,
      }),
    }),

    addToolDefinition: builder.mutation<
      AutopilotWorkflowModel,
      { autopilotWorkflow: AutopilotWorkflowModel; request: AddToolRequest }
    >({
      invalidatesTags: ['AutopilotWorkflow'],
      async onQueryStarted(
        { autopilotWorkflow, request },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          emailAutopilotApi.util.updateQueryData(
            'getOrCreateAutopilotWorkflow',
            {
              configurationId: autopilotWorkflow.intent_email_configuration_id,
              version: autopilotWorkflow.intent_email_configuration_version,
            },
            draft => {
              draft.tool_definitions = [
                ...draft.tool_definitions,
                {
                  description: '',
                  name: '',
                  tool_fields: {},
                  tool_id: request.tool_id,
                  tool_type: request.tool_type,
                },
              ];
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ autopilotWorkflow, request }) => ({
        body: request,
        method: 'PUT',
        url: `${SOLVE_AUTOPILOT_BASE_URL}/autopilot-workflow/${autopilotWorkflow.autopilot_workflow_id}/tools`,
      }),
    }),
    getAutopilotFlowDiagram: builder.query<
      FlowDiagramResponse,
      { autopilotWorkflowId: string }
    >({
      providesTags: (_result, _error, { autopilotWorkflowId }) => [
        { id: autopilotWorkflowId, type: 'FlowDiagram' },
      ],
      query: ({ autopilotWorkflowId }) =>
        `${SOLVE_AUTOPILOT_BASE_URL}/autopilot-workflow/${autopilotWorkflowId}/flow-diagram`,
    }),
    getAvailableTools: builder.query<AutoflowTool[], void>({
      query: () => ({
        url: `${SOLVE_AUTOPILOT_BASE_URL}/available-tools`,
      }),
    }),
    getOrCreateAutopilotWorkflow: builder.query<
      AutopilotWorkflowModel,
      GetOrCreateAutopilotWorkflowParams
    >({
      providesTags: ['AutopilotWorkflow'],
      query: ({ configurationId, version }) => ({
        url: `${SOLVE_AUTOPILOT_BASE_URL}/email-configuration/${configurationId}/version/${version}/autopilot-workflow`,
      }),
    }),
    getSupportGroups: builder.query<
      Array<{ default: boolean; id: number; is_public: boolean; name: string }>,
      void
    >({
      query: () => ({
        url: `${SOLVE_AUTOPILOT_BASE_URL}/support-groups`,
      }),
    }),
    refreshAutopilotFlowDiagram: builder.mutation<
      FlowDiagramResponse,
      { autopilotWorkflowId: string; previousOutline: FlowDiagramNode[] }
    >({
      // Write the response into the GET cache so subscribers see the new
      // outline without a refetch. `upsertQueryData` (rather than
      // `updateQueryData`) marks the cache entry as `fulfilled` — important
      // when the previous GET errored, since `updateQueryData` only swaps
      // `data` and would leave the error set, keeping the panel stuck on the
      // http-error view even after a successful refresh.
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            emailAutopilotApi.util.upsertQueryData(
              'getAutopilotFlowDiagram',
              { autopilotWorkflowId: args.autopilotWorkflowId },
              data,
            ),
          );
        } catch {
          // Errors surface via the panel's error state.
        }
      },
      query: ({ autopilotWorkflowId, previousOutline }) => ({
        body: { previous_outline: previousOutline },
        method: 'POST',
        url: `${SOLVE_AUTOPILOT_BASE_URL}/autopilot-workflow/${autopilotWorkflowId}/flow-diagram/refresh`,
      }),
    }),
    removeContextVariable: builder.mutation<
      AutopilotWorkflowModel,
      { autopilotWorkflow: AutopilotWorkflowModel; contextVariableId: string }
    >({
      invalidatesTags: ['AutopilotWorkflow'],
      async onQueryStarted(
        { autopilotWorkflow, contextVariableId },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          emailAutopilotApi.util.updateQueryData(
            'getOrCreateAutopilotWorkflow',
            {
              configurationId: autopilotWorkflow.intent_email_configuration_id,
              version: autopilotWorkflow.intent_email_configuration_version,
            },
            draft => {
              draft.context_variables = draft.context_variables.filter(
                variable => variable.context_variable_id !== contextVariableId,
              );
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ autopilotWorkflow, contextVariableId }) => ({
        method: 'DELETE',
        params: { context_variable_id: contextVariableId },
        url: `${SOLVE_AUTOPILOT_BASE_URL}/autopilot-workflow/${autopilotWorkflow.autopilot_workflow_id}/context-variables`,
      }),
    }),
    removeToolDefinition: builder.mutation<
      AutopilotWorkflowModel,
      { autopilotWorkflow: AutopilotWorkflowModel; toolId: string }
    >({
      invalidatesTags: ['AutopilotWorkflow'],
      async onQueryStarted(
        { autopilotWorkflow, toolId },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          emailAutopilotApi.util.updateQueryData(
            'getOrCreateAutopilotWorkflow',
            {
              configurationId: autopilotWorkflow.intent_email_configuration_id,
              version: autopilotWorkflow.intent_email_configuration_version,
            },
            draft => {
              draft.tool_definitions = draft.tool_definitions.filter(
                tool => tool.tool_id !== toolId,
              );
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ autopilotWorkflow, toolId }) => ({
        method: 'DELETE',
        params: { tool_id: toolId },
        url: `${SOLVE_AUTOPILOT_BASE_URL}/autopilot-workflow/${autopilotWorkflow.autopilot_workflow_id}/tools`,
      }),
    }),
    updateAutopilotPolicyDescription: builder.mutation<
      AutopilotWorkflowModel,
      { autopilotWorkflowId: string; policyDescription: string }
    >({
      invalidatesTags: ['AutopilotWorkflow'],
      query: ({ autopilotWorkflowId, policyDescription }) => {
        return {
          body: { policy_description: policyDescription },
          method: 'PUT',
          url: `${SOLVE_AUTOPILOT_BASE_URL}/autopilot-workflow/${autopilotWorkflowId}/policy-description`,
        };
      },
    }),
    updateBypassFallbackTriggers: builder.mutation<
      AutopilotWorkflowModel,
      {
        autopilotWorkflowId: string;
        bypassFallbackTriggers: boolean;
      }
    >({
      invalidatesTags: ['AutopilotWorkflow'],
      query: ({ autopilotWorkflowId, bypassFallbackTriggers }) => ({
        body: { bypass_fallback_triggers: bypassFallbackTriggers },
        method: 'PUT',
        url: `${SOLVE_AUTOPILOT_BASE_URL}/autopilot-workflow/${autopilotWorkflowId}/bypass-fallback-triggers`,
      }),
    }),
    updateMaxResponses: builder.mutation<
      AutopilotWorkflowModel,
      {
        autopilotWorkflowId: string;
        maxResponses: number | null;
      }
    >({
      invalidatesTags: ['AutopilotWorkflow'],
      query: ({ autopilotWorkflowId, maxResponses }) => ({
        body: { max_responses: maxResponses },
        method: 'PUT',
        url: `${SOLVE_AUTOPILOT_BASE_URL}/autopilot-workflow/${autopilotWorkflowId}/max-responses`,
      }),
    }),
    updateToolConfigurationFields: builder.mutation<
      AutopilotWorkflowModel,
      {
        autopilotWorkflowId: string;
        configurationFields: Record<string, unknown>;
        toolId: string;
      }
    >({
      invalidatesTags: ['AutopilotWorkflow'],
      query: ({ autopilotWorkflowId, configurationFields, toolId }) => ({
        body: { configuration_fields: configurationFields },
        method: 'PUT',
        url: `${SOLVE_AUTOPILOT_BASE_URL}/autopilot-workflow/${autopilotWorkflowId}/tools/${toolId}/configuration-fields`,
      }),
    }),
  }),
});

export const {
  useAddContextVariableMutation,
  useAddToolDefinitionMutation,
  useGetAutopilotFlowDiagramQuery,
  useGetAvailableToolsQuery,
  useGetOrCreateAutopilotWorkflowQuery,
  useGetSupportGroupsQuery,
  useRefreshAutopilotFlowDiagramMutation,
  useRemoveContextVariableMutation,
  useRemoveToolDefinitionMutation,
  useUpdateAutopilotPolicyDescriptionMutation,
  useUpdateBypassFallbackTriggersMutation,
  useUpdateMaxResponsesMutation,
  useUpdateToolConfigurationFieldsMutation,
} = emailAutopilotApi;
